import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Disc3, TrendingUp } from "lucide-react";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATE_INTERVAL_MS = 30_000; // 30 segundos (0:30)

function pickRandomIndex(len, exclude) {
  if (len <= 1) return 0;
  let idx = Math.floor(Math.random() * len);
  if (idx === exclude) idx = (idx + 1) % len;
  return idx;
}

export default function FeaturedReleases() {
  const [cds, setCds] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [remainingMs, setRemainingMs] = useState(ROTATE_INTERVAL_MS);
  const userPausedUntil = useRef(0);
  const cycleStartRef = useRef(Date.now());
  const { handlePlay } = useAudioPlayer();
  const navigate = useNavigate();

  // Fetch latest 12 approved CDs
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/cd-submissions/public?limit=24`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data.filter((c) => c.cover_url).slice(0, 12) : [];
        // Shuffle initial order for variety
        list.sort(() => Math.random() - 0.5);
        setCds(list);
        setActiveIdx(0);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate every 60s (random pick, never repeats consecutively)
  useEffect(() => {
    if (cds.length < 2) return undefined;
    cycleStartRef.current = Date.now();
    setRemainingMs(ROTATE_INTERVAL_MS);
    const tick = () => {
      if (Date.now() < userPausedUntil.current) return;
      setActiveIdx((cur) => pickRandomIndex(cds.length, cur));
      cycleStartRef.current = Date.now();
      setRemainingMs(ROTATE_INTERVAL_MS);
    };
    const rotateId = setInterval(tick, ROTATE_INTERVAL_MS);
    // Countdown clock updater (a cada 250ms = atualização suave do display)
    const clockId = setInterval(() => {
      if (Date.now() < userPausedUntil.current) {
        setRemainingMs(Math.max(0, userPausedUntil.current - Date.now()));
      } else {
        const elapsed = Date.now() - cycleStartRef.current;
        setRemainingMs(Math.max(0, ROTATE_INTERVAL_MS - elapsed));
      }
    }, 250);
    return () => {
      clearInterval(rotateId);
      clearInterval(clockId);
    };
  }, [cds.length, activeIdx]);

  const active = cds[activeIdx];

  // Prefetch tracks para o CD ativo (a lista pública não vem com tracks).
  // Necessário para o "OUÇA AGORA" tocar dentro do gesto do usuário (autoplay-safe).
  const [activeTracks, setActiveTracks] = useState([]);
  useEffect(() => {
    if (!active?.id) { setActiveTracks([]); return undefined; }
    let cancelled = false;
    fetch(`${API}/cd-submissions/${active.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((detail) => {
        if (cancelled || !detail) return;
        setActiveTracks(Array.isArray(detail.tracks) ? detail.tracks : []);
      })
      .catch(() => { if (!cancelled) setActiveTracks([]); });
    return () => { cancelled = true; };
  }, [active?.id]);

  // Always show 11 thumbs (excluding active)
  const thumbs = useMemo(() => {
    return cds.filter((_, i) => i !== activeIdx).slice(0, 11);
  }, [cds, activeIdx]);

  const handleThumbClick = (cd) => {
    const idx = cds.findIndex((c) => c.id === cd.id);
    if (idx !== -1) {
      setActiveIdx(idx);
      cycleStartRef.current = Date.now();
      setRemainingMs(ROTATE_INTERVAL_MS);
      userPausedUntil.current = Date.now() + ROTATE_INTERVAL_MS;
    }
  };

  const seconds = Math.ceil(remainingMs / 1000);
  const clockText = `0:${String(seconds).padStart(2, "0")}`;

  // Builds a play queue from the active CD tracks and starts playback
  // IMMEDIATELY (during click handler) so mobile/desktop don't block autoplay.
  const playActiveAndGo = (e) => {
    if (!active) return;
    e.preventDefault();
    const tracks = activeTracks.length > 0
      ? activeTracks
      : (Array.isArray(active.tracks) ? active.tracks : []);
    if (tracks.length === 0) {
      navigate(`/cd/${active.id}`);
      return;
    }
    const queue = tracks.map((t, idx) => ({
      id: t.id || `cd-${active.id}-${idx}`,
      title: t.title,
      artist_name: t.artist_name || active.artist_name,
      preview_url: t.audio_url || t.preview_url || "",
      audio_url: t.audio_url || t.preview_url || "",
      album_name: active.cd_title,
      album_cover: active.cover_url,
      _source_cd_id: active.id,
    }));
    // Start playback within user gesture (autoplay-safe)
    // Featured CD → escopo do artista: toca álbum inteiro, depois outros CDs do mesmo artista,
    // e só cai na rádio geral quando o catálogo do artista se esgotar.
    setRadioScope({ type: "artist", artistName: active.artist_name });
    handlePlay(queue[0], queue);
    // Then navigate to CD page so user sees full tracklist
    navigate(`/cd/${active.id}`);
  };

  if (loading) {
    return (
      <section className="px-4 sm:px-6 lg:px-12 py-12 max-w-7xl mx-auto" data-testid="featured-releases-loading">
        <div className="h-96 bg-white/5 rounded-2xl animate-pulse" />
      </section>
    );
  }

  if (!active) return null;

  return (
    <section
      className="px-4 sm:px-6 lg:px-12 py-12 max-w-7xl mx-auto"
      data-testid="featured-releases"
    >
      {/* Header */}
      <div className="flex items-end justify-between mb-6 border-b-2 border-[#FF007F] pb-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="text-[#FF007F]" size={26} />
          <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
            LANÇAMENTOS EM DESTAQUE
          </h2>
        </div>
        <Link
          to="/playlists"
          className="text-xs sm:text-sm text-zinc-400 hover:text-[#FF007F] font-bold uppercase tracking-wider transition-colors"
          data-testid="featured-see-all"
        >
          Ver todos →
        </Link>
      </div>

      {/* Showcase grande */}
      <a
        href={`/cd/${active.id}`}
        onClick={playActiveAndGo}
        className="group block relative aspect-[16/10] sm:aspect-[21/9] lg:aspect-[21/8] rounded-2xl overflow-hidden bg-zinc-900 shadow-2xl shadow-[#FF007F]/20 ring-1 ring-white/10 cursor-pointer"
        data-testid="featured-active"
        key={active.id}
      >
        {/* Background: capa desfocada esticada */}
        <img
          src={active.cover_url.startsWith("http") ? active.cover_url : `${process.env.REACT_APP_BACKEND_URL}${active.cover_url}`}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-50"
          loading="eager"
        />
        {/* Overlay escuro pra texto ler bem */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Layout split: capa à esquerda + info à direita */}
        <div className="relative h-full flex items-center gap-4 sm:gap-8 p-4 sm:p-8">
          {/* Capa quadrada nítida */}
          <div className="aspect-square h-full max-h-[260px] sm:max-h-[300px] lg:max-h-[340px] rounded-xl overflow-hidden shadow-2xl ring-2 ring-[#FF007F]/60 flex-shrink-0 transition-transform duration-700 group-hover:scale-[1.03] group-hover:ring-[#FF007F]">
            <img
              src={active.cover_url.startsWith("http") ? active.cover_url : `${process.env.REACT_APP_BACKEND_URL}${active.cover_url}`}
              alt={active.cd_title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <p className="text-[#FF007F] text-[10px] sm:text-sm font-black uppercase tracking-widest mb-1 sm:mb-2 drop-shadow-lg truncate">
              {active.artist_name}
            </p>
            <h3 className="font-heading text-xl sm:text-3xl lg:text-5xl font-black text-white leading-tight drop-shadow-2xl line-clamp-3">
              {active.cd_title}
            </h3>
            <p className="hidden sm:flex items-center gap-2 mt-2 text-zinc-300 text-sm">
              <Disc3 size={14} /> {active.tracks_count ?? active.tracks?.length ?? 0} faixas
            </p>

            <div className="mt-3 sm:mt-5">
              <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-[#FF007F] text-white font-black text-xs sm:text-base shadow-lg shadow-[#FF007F]/50 group-hover:scale-105 group-hover:bg-[#FF3399] transition-all">
                <Play size={14} className="fill-current" /> OUÇA AGORA
              </div>
            </div>
          </div>
        </div>

        {/* Indicador de timer (barra que enche) + relógio */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/5 overflow-hidden">
          <div
            key={`${active.id}-bar`}
            className="h-full bg-[#FF007F] origin-left"
            style={{
              animation: `featuredProgress ${ROTATE_INTERVAL_MS}ms linear forwards`,
            }}
          />
        </div>
        <div
          className="absolute top-2 right-3 sm:top-3 sm:right-4 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-[10px] sm:text-xs font-black text-[#FF007F] tabular-nums tracking-wider flex items-center gap-1 border border-[#FF007F]/40 shadow-lg shadow-black/40"
          data-testid="featured-clock"
          title="Tempo até o próximo destaque"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" />
          {clockText}
        </div>
      </a>

      {/* Thumbnails grid */}
      {thumbs.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-2 sm:gap-3 mt-4">
          {thumbs.map((cd) => (
            <button
              key={cd.id}
              onClick={() => handleThumbClick(cd)}
              className="group relative aspect-square rounded-lg overflow-hidden bg-zinc-900 ring-1 ring-white/10 hover:ring-[#FF007F] transition-all hover:scale-105"
              title={`${cd.artist_name} – ${cd.cd_title}`}
              data-testid={`featured-thumb-${cd.id}`}
            >
              <img
                src={cd.cover_url.startsWith("http") ? cd.cover_url : `${process.env.REACT_APP_BACKEND_URL}${cd.cover_url}`}
                alt={cd.cd_title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-1 left-1 right-1 text-white text-[9px] sm:text-[10px] font-bold leading-tight line-clamp-2 drop-shadow-lg">
                {cd.cd_title}
              </div>
            </button>
          ))}
        </div>
      )}

      <style>{`
        @keyframes featuredProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
